import featureFlagModule, { FeatureFlagKeys } from '@modules/FeatureFlagModule';
import getUsers from '../api/users/getUsers';
import ServiceUser from '../types/ServiceUser';
import refreshUser from './refreshUser';
import { serviceState } from './state';
import { logException } from './log';

/**
 * Thrown when an agent cannot be resolved from the session roster. Expected for
 * account-wide RTCS broadcasts (agent.status), so callers handling those should
 * catch it rather than report it.
 */
export class UserNotFoundError extends Error {
  constructor(readonly userId: string) {
    super(`No roster user found for contactCenterUserId ${userId}`);
    this.name = 'UserNotFoundError';
  }
}

export function getUser(userId: string): ServiceUser | null {
  return serviceState.userById[userId] || null;
}

export function getUserByContactCenterUserId(
  contactCenterUserId: string,
): ServiceUser | null {
  return serviceState.userByContactCenterUserId[contactCenterUserId] || null;
}

/** Prefer the roster object; prepare may have held a pointer refreshUsers replaced. */
export function rosterUser(
  contactCenterUserId: string,
  fallback: ServiceUser,
): ServiceUser {
  return getUserByContactCenterUserId(contactCenterUserId) ?? fallback;
}

async function refreshUsers(): Promise<void> {
  const freshUsers = await getUsers(serviceState.id ?? '');
  serviceState.users = freshUsers.map((u) => {
    const existing = u.contactCenterUserId
      ? getUserByContactCenterUserId(u.contactCenterUserId)
      : null;
    if (!existing) return u;
    return {
      ...u,
      stats: u.stats ?? existing.stats,
      activityStatus: existing.activityStatus,
      availabilityStatus: existing.availabilityStatus,
    };
  });
}

async function requireUser(userId: string): Promise<ServiceUser> {
  let user = getUserByContactCenterUserId(userId);
  if (!user) {
    const singleItemEnabled = featureFlagModule.isEnabled(
      FeatureFlagKeys.featureRtcsSingleItemFetch,
    );

    // Single-item fetch first when flag-on; isolate its failure so the
    // list-refresh fallback below still runs when it throws (not just nulls).
    if (singleItemEnabled) {
      try {
        const fetched = await refreshUser(userId);
        if (fetched) {
          user = getUserByContactCenterUserId(userId);
        }
      } catch (err) {
        logException(err, 'rtcs:user:refresh:single-item-failed');
      }
    }

    // Last-resort list refetch when single-item is disabled or did not surface the user.
    if (!user) {
      try {
        await refreshUsers();
      } catch (err) {
        logException(err, 'Failed to refresh users');
      }
      user = getUserByContactCenterUserId(userId);
    }
  }
  if (!user) {
    console.error(
      `A websocket event did not find a valid user account:: ${userId}, try refreshing your browser.`,
    );
    throw new UserNotFoundError(userId);
  }
  return user;
}

export default requireUser;
